import React from 'react'
import type { GetServerSidePropsContext } from 'next'
import api from 'core/api/contentstack'
import syndigoApi from 'core/api/syndigo'
import references from 'core/api/references'
import Listing from 'core/components/Listing'
import { layoutData } from 'core/layout'
import { ProductCard } from 'core/components/Card'
import { RecipeCard } from 'core/components/Card'
import { toContentStack } from './api/syndigo/transformations'
import { short } from 'core/helpers/string'
import { toListingItem, toRecipeListingItem } from 'core/helpers/listing'

// Per-locale cache for the transformed + sorted product list.
// toContentStack() + sort are deterministic for a given locale and only need
// to run once per cache window, not on every search request.
// Keyed by locale — at most 3 entries (en-us, en-ca, fr-ca), so no size cap needed.
// TTL matches the Syndigo listingCache so both expire together.
const TRANSFORMED_PRODUCTS_TTL_MS = 12 * 60 * 60 * 1000 // 12 hours
const transformedProductsCache = new Map<string, { items: any[]; expiresAt: number }>()

// Per-locale cache for the mapped recipe listing items.
// The raw recipe data is already cached by contentListingCache (12h TTL) in
// contentstack.ts. What's uncached is the toRecipeListingItem() projection
// that runs on every request over all recipes. Caching the mapped output
// avoids that per-request allocation entirely.
// Keyed by locale — at most 3 entries, no size cap needed.
// TTL matches contentListingCache so both expire together.
const TRANSFORMED_RECIPES_TTL_MS = 12 * 60 * 60 * 1000 // 12 hours
const transformedRecipesCache = new Map<string, { items: any[]; expiresAt: number }>()

const Search = (props: any) => {
  const Card = props.prefix === 'products' ? ProductCard : RecipeCard
  return <Listing {...props} card={Card} useInfiniteScroll={true} />
}

export default Search

/**
 * Check if a keyword exists in certain fields of a product/recipe.
 * Mirrors the keywordInFields logic in Listing.tsx so results are consistent.
 */
function keywordInFields(item: any, keyword: string): boolean {
  const fields = ['title', 'description', 'species', 'product_code', 'gtin']
  const lowerKeyword = keyword.toLowerCase()
  return fields.some((field) => {
    return item[field] != null && typeof item[field] === 'string' && item[field].toLowerCase().includes(lowerKeyword)
  })
}

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const keyword = (context.query.keyword as string) || ''
  const type = (context.query.type as string) || 'products'
  const prefix = type === 'recipes' ? 'recipes' : 'products'

  // Block excessively long keywords
  if (keyword.length > 100) {
    return { notFound: true }
  }

  // Prevent CDN/ISR caching — the search page is SSR-only
  context.res.setHeader('Cache-Control', 'private, no-store')

  const locale = context.locale?.toLowerCase() === 'fr' ? 'fr-ca' : (context.locale?.toLowerCase() || 'en-us')

  if (prefix === 'products') {
    // syndigoApi.getProductListing has a 12h in-memory cache — shared across
    // all search requests and ISR rebuilds, so this is fast after the first call.
    let syndigoResults: any, result: any, layout: any
    try {
      ;[syndigoResults, result, layout] = await Promise.all([
        syndigoApi.getProductListing(locale, 0, 380),
        api.getEntryByUrl('listing', '/products', context.locale || 'en-US', [
          ...references.PRODUCT_LISTING,
          'featured_products.product',
        ]),
        layoutData(api, context.locale || 'en-US'),
      ])
    } catch (err) {
      console.error('getServerSideProps (search/products): fetch failed', err)
      return { notFound: true }
    }

    // Serve transformed + sorted product list from cache when fresh.
    // toContentStack() allocates ~380 objects per call — caching avoids that
    // on every search request and only re-runs when the underlying Syndigo
    // data would also be re-fetched (same 12h window).
    const cached = transformedProductsCache.get(locale)
    let allItems: any[]
    if (cached && Date.now() < cached.expiresAt) {
      allItems = cached.items
    } else {
      allItems = syndigoResults ? toContentStack(locale, syndigoResults.Results, true) : []
      allItems = allItems.map((item: any) => {
        item.short_title = short(item.title)
        return item
      })
      allItems = allItems.sort((a: any, b: any) =>
        !a.short_title ? 1 : !b.short_title ? -1 : a.short_title.localeCompare(b.short_title)
      )
      transformedProductsCache.set(locale, { items: allItems, expiresAt: Date.now() + TRANSFORMED_PRODUCTS_TTL_MS })
    }

    let items = keyword
      ? allItems.filter((item: any) => keywordInFields(item, keyword))
      : allItems

    if (!result) return { notFound: true }

    return {
      props: {
        layout: true,
        ...layout,
        items: items.map(toListingItem).filter(Boolean),
        result,
        prefix: 'products',
      },
    }
  }

  // Recipes
  let rawRecipes: any, result: any, layout: any
  try {
    ;[rawRecipes, result, layout] = await Promise.all([
      api.getEntriesByContentTypeFilter('recipe', context.locale || 'en-US', undefined, [
        'filters',
        'product',
        'product.color',
      ]),
      api.getEntryByUrl('listing', '/recipes', context.locale || 'en-US', references.RECIPE_LISTING),
      layoutData(api, context.locale || 'en-US'),
    ])
  } catch (err) {
    console.error('getServerSideProps (search/recipes): fetch failed', err)
    return { notFound: true }
  }

  // Serve mapped recipe items from cache when fresh.
  // toRecipeListingItem() projects every recipe on every request — caching
  // avoids that per-request allocation and only re-runs when the underlying
  // Contentstack data would also be re-fetched (same 12h window).
  // Cache key uses context.locale (the raw Next.js locale) to match
  // getEntriesByContentTypeFilter's cache key.
  const recipeCacheKey = (context.locale || 'en-US').toLowerCase()
  const cachedRecipes = transformedRecipesCache.get(recipeCacheKey)
  let allRecipes: any[]
  if (cachedRecipes && Date.now() < cachedRecipes.expiresAt) {
    allRecipes = cachedRecipes.items
  } else {
    allRecipes = ((rawRecipes as any[]) || []).map(toRecipeListingItem).filter(Boolean)
    transformedRecipesCache.set(recipeCacheKey, { items: allRecipes, expiresAt: Date.now() + TRANSFORMED_RECIPES_TTL_MS })
  }

  const filtered = keyword
    ? allRecipes.filter((item: any) => {
        const lowerKeyword = keyword.toLowerCase()
        const title = item.title?.toLowerCase() || ''
        const description = item.description?.toLowerCase() || ''
        return title.includes(lowerKeyword) || description.includes(lowerKeyword)
      })
    : allRecipes

  if (!result) return { notFound: true }

  return {
    props: {
      layout: true,
      ...layout,
      items: filtered,
      result,
      prefix: 'recipes',
    },
  }
}
